import React, { useState, useEffect, useRef, useContext } from "react";
import { Zap, Clock, ShoppingCart, ChevronLeft, ChevronRight, Star, TrendingUp, Flame, Package, AlertCircle } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import gsap from "gsap";
import { useTheme } from "components/Theme/ThemeProvider";
import { summaryApi } from "common";
import addToCart from "helpers/addToCart";
import Context from "context";


// Countdown Timer Component
function CountdownTimer({ endDate, colors }) {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [isExpired, setIsExpired] = useState(false);

  useEffect(() => {
    const calculateTimeLeft = () => {
      const difference = new Date(endDate) - new Date();
      
      if (difference <= 0) {
        setIsExpired(true);
        return;
      }

      setTimeLeft({
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((difference / 1000 / 60) % 60),
        seconds: Math.floor((difference / 1000) % 60)
      });
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(timer);
  }, [endDate]);

  if (isExpired) {
    return (
      <div className="flex items-center gap-2 text-sm" style={{ color: colors.error }}>
        <AlertCircle className="w-4 h-4" />
        Deal Expired
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      {[
        { label: "D", value: timeLeft.days },
        { label: "H", value: timeLeft.hours },
        { label: "M", value: timeLeft.minutes },
        { label: "S", value: timeLeft.seconds }
      ].map((item, index) => (
        <React.Fragment key={item.label}>
          <motion.div
            initial={{ scale: 0.8 }}
            animate={{ scale: 1 }}
            className="flex flex-col items-center min-w-[40px] px-2 py-1 rounded-lg"
            style={{
              backgroundColor: colors.bgSecondary,
              border: `1px solid ${colors.border}`
            }}
          >
            <span className="text-lg font-bold" style={{ color: colors.primary }}>
              {String(item.value).padStart(2, "0")}
            </span>
            <span className="text-[10px]" style={{ color: colors.textSecondary }}>
              {item.label}
            </span>
          </motion.div>
          {index < 3 && <span style={{ color: colors.textSecondary }}>:</span>}
        </React.Fragment>
      ))}
    </div>
  );
}

// Deal Card Component
function DealCard({ deal, product, colors, onAddToCart }) {
  const cardRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    if (cardRef.current) {
      gsap.fromTo(
        cardRef.current,
        { opacity: 0, y: 30 },
        { opacity: 1, y: 0, duration: 0.6, ease: "power3.out" }
      );
    }
  }, []);

  const discountPercentage = Math.round((product.dealDiscount / product.productId.productPrice) * 100);
  const stockPercentage = product.stockLimit 
    ? Math.round((product.soldCount / product.stockLimit) * 100)
    : 0;

  return (
    <motion.div
      ref={cardRef}
      whileHover={{ y: -10 }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      className="relative rounded-2xl overflow-hidden group cursor-pointer"
      style={{
        backgroundColor: colors.bgSecondary,
        border: `2px solid ${colors.border}`
      }}
    >
      {/* Discount Badge */}
      <motion.div
        initial={{ scale: 0, rotate: -180 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={{ delay: 0.2, type: "spring" }}
        className="absolute top-4 left-4 z-10 px-3 py-1 rounded-full flex items-center gap-1 font-bold text-sm shadow-lg"
        style={{
          background: `linear-gradient(135deg, ${colors.error} 0%, ${colors.accent} 100%)`,
          color: "white"
        }}
      >
        <Flame className="w-4 h-4" />
        {discountPercentage}% OFF
      </motion.div>

      {/* Image */}
      <div className="relative h-64 overflow-hidden">
        <motion.img
          src={product.productId.productImage[0]}
          alt={product.productId.productName}
          className="w-full h-full object-cover"
          animate={{ scale: isHovered ? 1.1 : 1 }}
          transition={{ duration: 0.4 }}
        />
        <div
          className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity"
          style={{
            background: `linear-gradient(to top, ${colors.bg} 0%, transparent 50%)`
          }}
        />
      </div>

      {/* Content */}
      <div className="p-5 space-y-4">
        {/* Product Name & Brand */}
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: colors.accent }}>
            {product.productId.productBrand}
          </p>
          <h3 className="text-lg font-bold line-clamp-2" style={{ color: colors.text }}>
            {product.productId.productName}
          </h3>
        </div>

        {/* Pricing */}
        <div className="space-y-2">
          <div className="flex items-center gap-3">
            <span className="text-3xl font-bold" style={{ color: colors.primary }}>
              ₦{product.dealPrice}
            </span>
            <span className="text-lg line-through" style={{ color: colors.textSecondary }}>
              ₦{product.productId.productPrice}
            </span>
          </div>
          <p className="text-sm font-semibold" style={{ color: colors.success }}>
            Save ₦{product.dealDiscount}
          </p>
        </div>

        {/* Stock Progress */}
        {product.stockLimit && (
          <div className="space-y-2">
            <div className="flex items-center justify-between text-xs">
              <span style={{ color: colors.textSecondary }}>
                {product.stockLimit - product.soldCount} left
              </span>
              <span style={{ color: colors.textSecondary }}>
                {product.soldCount} sold
              </span>
            </div>
            <div className="h-2 rounded-full overflow-hidden" style={{ backgroundColor: colors.border }}>
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${stockPercentage}%` }}
                transition={{ duration: 1, delay: 0.3 }}
                className="h-full rounded-full"
                style={{
                  background: stockPercentage > 80
                    ? `linear-gradient(90deg, ${colors.error} 0%, ${colors.accent} 100%)`
                    : `linear-gradient(90deg, ${colors.primary} 0%, ${colors.accent} 100%)`
                }}
              />
            </div>
            {stockPercentage > 80 && (
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-xs font-semibold flex items-center gap-1"
                style={{ color: colors.error }}
              >
                <TrendingUp className="w-3 h-3" />
                Almost gone! Hurry up!
              </motion.p>
            )}
          </div>
        )}

        {/* Add to Cart Button */}
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
           onClick={(e) => onAddToCart(e, product)}
          className="w-full py-3 rounded-xl font-bold text-white flex items-center justify-center gap-2 shadow-lg"
          style={{
            background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.accent} 100%)`
          }}
        >
          <ShoppingCart className="w-5 h-5" />
          Add to Cart
        </motion.button>
      </div>

      {/* Hover Glow Effect */}
      <motion.div
        className="absolute inset-0 pointer-events-none"
        initial={{ opacity: 0 }}
        animate={{ opacity: isHovered ? 1 : 0 }}
        style={{
          background: `radial-gradient(circle at 50% 50%, ${colors.primary}15 0%, transparent 70%)`
        }}
      />
    </motion.div>
  );
}

// Main Flash Deals Component
function FlashDeals() {
    const { fetchUserAddToCart } = useContext(Context);
  const { colors } = useTheme();
  const [deals, setDeals] = useState([]);
  const [currentDealIndex, setCurrentDealIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const headerRef = useRef(null);
  const titleRef = useRef(null);
  

  useEffect(() => {
    fetchActiveDeals();
    animateHeader();
  }, []);

  const animateHeader = () => {
    if (titleRef.current) {
      gsap.fromTo(
        titleRef.current,
        { opacity: 0, y: -50 },
        { opacity: 1, y: 0, duration: 1, ease: "power3.out" }
      );
    }

    if (headerRef.current) {
      const flames = headerRef.current.querySelectorAll('.flame-icon');
      gsap.fromTo(
        flames,
        { scale: 0, rotation: -180 },
        { 
          scale: 1, 
          rotation: 0, 
          duration: 0.8, 
          stagger: 0.1,
          ease: "elastic.out(1, 0.5)"
        }
      );
    }
  };

  const fetchActiveDeals = async () => {
    try {
      setLoading(true);
      const response = await fetch(summaryApi.getActiveDeals.url, {
        method: summaryApi.getActiveDeals.method,
        
      });
      const data = await response.json();
      
      if (data.success) {
        setDeals(data.data || []);
      }
    } catch (error) {
      console.error("Error fetching deals:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = async (e, product) => {
        e.preventDefault();
        e.stopPropagation();

        try {
            // Create proper cart data structure
            const cartData = {
                productId: product.productId._id, // Use the actual product ID
                storage: product.productId.storage || '',
                color: product.productId.color || '',
                ram: product.productId.ram || '',
                processor: product.productId.processor || '',
                screenSize: product.productId.screenSize || '',
                // Include deal-specific information if needed
                dealPrice: product.dealPrice,
                originalPrice: product.productId.productPrice
            };

            console.log("Adding to cart:", {
                productName: product.productId.productName,
                productId: product.productId._id,
                dealPrice: product.dealPrice
            });

            await addToCart(e, cartData, fetchUserAddToCart);
            
        } catch (error) {
            console.error("Error in handleAddToCart:", error);
        }
    };

  
  const currentDeal = deals[currentDealIndex];

    const nextDeal = () => {
        setCurrentDealIndex((prev) => (prev + 1) % deals.length);
    };

    const prevDeal = () => {
        setCurrentDealIndex((prev) => (prev - 1 + deals.length) % deals.length);
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: colors.bg }}>
                <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                    className="p-6 rounded-full"
                    style={{ backgroundColor: `${colors.primary}20` }}
                >
                    <Zap className="w-12 h-12" style={{ color: colors.primary }} />
                </motion.div>
            </div>
        );
    }

    if (!deals.length) {
        return (
            <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: colors.bg }}>
                <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="text-center space-y-4 p-8 rounded-2xl"
                    style={{ backgroundColor: colors.bgSecondary, border: `1px solid ${colors.border}` }}
                >
                    <Package className="w-16 h-16 mx-auto" style={{ color: colors.textSecondary }} />
                    <h3 className="text-2xl font-bold" style={{ color: colors.text }}>
                        No Active Deals
                    </h3>
                    <p style={{ color: colors.textSecondary }}>
                        Check back soon for amazing flash deals!
                    </p>
                </motion.div>
            </div>
        );
    }

    return (
        <div className="min-h-screen py-12 px-4" style={{ backgroundColor: colors.bg }}>
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div ref={headerRef} className="text-center mb-12 space-y-4">
                    <motion.div
                        ref={titleRef}
                        className="flex items-center justify-center gap-4"
                    >
                        <Flame className="flame-icon w-12 h-12" style={{ color: colors.error }} />
                        <h1 className="text-5xl md:text-6xl font-black" style={{ color: colors.text }}>
                            Flash Deals
                        </h1>
                        <Zap className="flame-icon w-12 h-12" style={{ color: colors.accent }} />
                    </motion.div>
                    
                    <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.5 }}
                        className="text-xl"
                        style={{ color: colors.textSecondary }}
                    >
                        Limited time offers you can't miss! ⚡
                    </motion.p>
                </div>

                {/* Deal Navigation */}
                {deals.length > 1 && (
                    <div className="flex items-center justify-center gap-4 mb-8">
                        <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={prevDeal}
                            className="p-3 rounded-full shadow-lg"
                            style={{ backgroundColor: colors.primary }}
                        >
                            <ChevronLeft className="w-6 h-6 text-white" />
                        </motion.button>

                        <div className="flex gap-2">
                            {deals.map((_, index) => (
                                <motion.button
                                    key={index}
                                    whileHover={{ scale: 1.2 }}
                                    onClick={() => setCurrentDealIndex(index)}
                                    className="rounded-full transition-all"
                                    style={{
                                        width: currentDealIndex === index ? "32px" : "12px",
                                        height: "12px",
                                        backgroundColor: currentDealIndex === index ? colors.primary : colors.border
                                    }}
                                />
                            ))}
                        </div>

                        <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={nextDeal}
                            className="p-3 rounded-full shadow-lg"
                            style={{ backgroundColor: colors.primary }}
                        >
                            <ChevronRight className="w-6 h-6 text-white" />
                        </motion.button>
                    </div>
                )}

                <AnimatePresence mode="wait">
                    {currentDeal && (
                        <motion.div
                            key={currentDealIndex}
                            initial={{ opacity: 0, x: 100 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -100 }}
                            transition={{ duration: 0.5 }}
                            className="space-y-8"
                        >
                            {/* Deal Header */}
                            <div
                                className="relative p-8 rounded-2xl overflow-hidden"
                                style={{
                                    background: `linear-gradient(135deg, ${colors.primary}20 0%, ${colors.accent}20 100%)`,
                                    border: `2px solid ${colors.border}`
                                }}
                            >
                                <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-6">
                                    <div className="space-y-2">
                                        <div className="flex items-center gap-2">
                                            <Star className="w-6 h-6" style={{ color: colors.accent }} />
                                            <span className="text-sm font-semibold uppercase tracking-wider" style={{ color: colors.accent }}>
                                                {currentDeal.dealType.replace('_', ' ')}
                                            </span>
                                        </div>
                                        <h2 className="text-3xl md:text-4xl font-black" style={{ color: colors.text }}>
                                            {currentDeal.dealName}
                                        </h2>
                                        <p className="text-lg" style={{ color: colors.textSecondary }}>
                                            {currentDeal.dealDescription}
                                        </p>
                                    </div>

                                    <div className="flex flex-col items-center gap-3">
                                        <div className="flex items-center gap-2 text-sm font-semibold" style={{ color: colors.text }}>
                                            <Clock className="w-5 h-5" style={{ color: colors.primary }} />
                                            Ends In:
                                        </div>
                                        <CountdownTimer endDate={currentDeal.endDate} colors={colors} />
                                    </div>
                                </div>

                                {/* Animated Background */}
                                <motion.div
                                    animate={{
                                        scale: [1, 1.2, 1],
                                        rotate: [0, 180, 360]
                                    }}
                                    transition={{
                                        duration: 20,
                                        repeat: Infinity,
                                        ease: "linear"
                                    }}
                                    className="absolute -top-20 -right-20 w-64 h-64 rounded-full opacity-20"
                                    style={{ backgroundColor: colors.primary }}
                                />
                                <motion.div
                                    animate={{
                                        scale: [1.2, 1, 1.2],
                                        rotate: [360, 180, 0]
                                    }}
                                    transition={{
                                        duration: 15,
                                        repeat: Infinity,
                                        ease: "linear"
                                    }}
                                    className="absolute -bottom-20 -left-20 w-64 h-64 rounded-full opacity-20"
                                    style={{ backgroundColor: colors.accent }}
                                />
                            </div>

                            {/* Products Grid */}
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                                {currentDeal.products.map((product, index) => (
                                    <motion.div
                                        key={product.productId._id}
                                        initial={{ opacity: 0, y: 30 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: index * 0.1 }}
                                    >
                                        <DealCard
                                            deal={currentDeal}
                                            product={product}
                                            colors={colors}
                                            onAddToCart={handleAddToCart} // Use the fixed function
                                        />
                                    </motion.div>
                                ))}
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </div>
    );
}

export default FlashDeals;